"use strict";

import constraints from './calculator/helpers/constraints';
import {between, floorf, isset, moment_difference, pad, roundf} from './js-utilities';
import WaistManager from './calculator/waist-manager';
import SmokingManager from './calculator/smoking-manager';
import VitaminDManager from './calculator/vitamin-d-manager';
import FmdManager from './calculator/fmd-manager';
import A1cManager from './calculator/a1c-manager';
import loadLanguage from './language';
import stringConstants from './calculator/helpers/string-constants';
import BmiManager from './calculator/bmi-manager';
import LipidsManager from './calculator/lipids-manager';
import {
    bmiFromHeightAndWeight,
    mapClientToServerValues,
    mapServerToClientValues,
    parseInput
} from './calculator/helpers/calculator-helpers';
import BpManager from './calculator/bp-manager';
import * as Sentry from '@sentry/browser';
import {Integrations} from '@sentry/tracing';
import EVCalculator from "./calculator/EV/EVCalculator";

export default class CalculatorController {
    gradientCache = {};
    isDirty = false;

    init() {
        const appProduct = $('meta[name="appProduct"]').prop('content');
        const viewName = $('meta[name="viewName"]').prop('content');
        const langCode = $('meta[name="langCode"]').prop('content');
        const partnerSubdomain = $('meta[name="partnerSubdomain"]').prop('content');
        this.language = loadLanguage(appProduct, viewName, langCode, partnerSubdomain);
        this.calculator = new VascularAgeCalculator(3.3);
        this.calculator_4 = new EVCalculator(4);
        this.calculator_4.init();
        // Set up storage
        this.resetCurrentValues();
        this.bindHighlightEmptyRequiredFields();
        this.current_age_changes = {};
        this.inAutoupdate = false;
        this.skippedUpdates = 0;
        this.development_mode = false;
        this.constraints = constraints(this.language);

        // Activate Sentry Reporting
        if (process.env.MIX_SENTRY_LARAVEL_DSN) {
            Sentry.init({
                dsn: process.env.MIX_SENTRY_LARAVEL_DSN,
                environment: process.env.MIX_SENTRY_ENVIRONMENT || process.env.MIX_APP_ENV,
                tracesSampleRate: process.env.MIX_SENTRY_TRACES_SAMPLE_RATE || 1,
                integrations: [new Integrations.BrowserTracing()],
            });
        }
    }


    init_ui() {
        if (vac_public === false && isset(this.login_setup)) {
            this.login_setup();
            return;
        }
        this.init_ui2();
    }

    // Called after user accepts terms of use.
    init_ui2() {
        // Load date selects.
        const month_select = document.getElementById('month_select');
        month_select.options[0] = new Option('-', '', true);
        month_select.options[0].disabled = true;
        for (let month = 1; month <= 12; month++) {
            month_select.options[month_select.options.length] = new Option(month, month, false);
        }

        this.populate_month_select();

        const year_select = document.getElementById('year_select');
        year_select.options[0] = new Option('-', '', true);
        year_select.options[0].disabled = true;
        // Offer everything over 18 years ago.
        const today = new Date();
        const current_year = today.getFullYear();
        for (let year = current_year - 15; year > current_year - 101; year--) {
            year_select.options[year_select.options.length] = new Option(year, year, false);
        }
        this.update_days();

        const gender_select = document.getElementById('gender_select');
        // The CARC won't really work without gender, but it's throwing errors for some people so at least check if it's null
        if (gender_select) {
            gender_select.options[0] = new Option('-', '', true);
            gender_select.options[0].disabled = true;
            gender_select.options[1] = new Option(this.language.female_label, 1, false);
            gender_select.options[2] = new Option(this.language.male_label, 0, false);
        }

        $('#gender_select').on('change', this.gender_changed.bind(this));
        $('#global_units_select').on('change', this.global_units_changed.bind(this));
        $('#day_select, #month_select, #year_select').on('change', this.birthday_changed.bind(this));

        document.getElementById('print_button').addEventListener('click', (event) => {
            this.print_report();
        });
        if (document.getElementById('share_chart')) {
            document.getElementById('share_chart').addEventListener('click', (event) => {
                if (!document.getElementById('share_chart').classList.contains('bordered_button_disabled')) {
                    this.open_dialog('chartModal');
                }
            });
        }
        const loginTop = document.getElementById('login_button_top');
        if (loginTop) {
            loginTop.addEventListener('click', (event) => {
                clearSaveClicked();
                $('#loginModal #savedata').val(0);
                $('.modal').modal('hide');
                this.open_dialog('loginModal');
            });
        }

        function clearSaveClicked() {
            if (typeof (window.sessionStorage) !== 'undefined') {
                window.sessionStorage.removeItem('save_clicked');
            }
        }

        document.getElementById('login_button').addEventListener('click', (event) => {
            $('.modal').modal('hide');
            this.open_dialog('loginModal');
        });
        document.getElementById('forgot_password').addEventListener('click', (event) => {
            $('.modal').modal('hide');
            this.open_dialog('resetModal');
        });
        document.getElementById('register_button').addEventListener('click', (event) => {
            event.preventDefault();
            if (!this.validate_age()) {
                this.open_dialog('dobRequiredModal');
                return;
            }
            $('.modal').modal('hide');
            this.open_dialog('registerModal');
        });

        $('#introModal').on('hidden.bs.modal', function () {
            let $this = $(this);
            $this.find("video").get(0).pause();
            $this.modal('hide');
            $('#disclaimerModal').modal('show');
        });

        $(document).on("click", "#save_button, #share_button, #dietid_link, #symptoms_tracker_link", this.saveData.bind(this));

        $(document).on("click", "#goto_dietid", e => {
            e.preventDefault();
            this.openDietId();
        });

        // Button inside Symptoms Tracker modal, after save
        $(document).on("click", "#goto_symptomstracker", e => {
            e.preventDefault();
            this.openSymptomsTracker();
        });

        this.bmiManager = new BmiManager(this, this.language);
        this.waistManager = new WaistManager(this, this.language);
        this.bpManager = new BpManager(this, this.language);
        this.smokingManager = new SmokingManager(this, this.language);
        this.vitaminDManager = new VitaminDManager(this, this.language);
        this.fmdManager = new FmdManager(this, this.language);
        this.a1cManager = new A1cManager(this, this.language);
        this.lipidsManager = new LipidsManager(this, this.language);

        document.getElementById('showFields').addEventListener('click', (event) => {
            this.showInputFields(event.currentTarget.checked);
        });
    }

    openDietId() {
        $.ajax({
            url: '/getDietIDToken',
            type: 'get',
            dataType: 'text',
            success: response => {
                let link = response;
                $('#savedModal').modal('hide');
                window.open(link, '_blank');
            }
        });
    }

    openSymptomsTracker() {
        $('#symptomsModal').modal('hide');
        window.open('/symptoms-tracker', '_blank');
    }

    set(field, value) {
        this.isDirty = true;
        this.current_values[field] = value;
    }

    //
    // Cell handling.
    //

    populate_month_select() {
        const month_select = document.getElementById('month_select');
        const day_select = document.getElementById('day_select');
        const year_select = document.getElementById('year_select');
        const month_value = parseInput(month_select.value, 'int');
        let year_value = parseInput(year_select.value, 'int');
        // Put in a leap year value so days load correctly if year is not selected yet.
        if (year_value < 0) {
            year_value = 2000;
        }
        let days_in_month = 31;
        if (month_value === 2) {
            days_in_month = (year_value % 4 === 0 && (year_value % 100 !== 0 || year_value % 400 === 0)) ? 29 : 28;
        } else if (month_value === 4 || month_value === 6 || month_value === 9 || month_value === 11) {
            days_in_month = 30;
        }
        // Add/remove options from days select list as needed
        if (day_select.options.length > days_in_month + 1) { // Remove excess days, if needed
            day_select.options.length = days_in_month + 1;
        } else if (day_select.options.length < days_in_month + 1) {
            if (!day_select.options[0]) {
                day_select.options[0] = new Option('-', '', true);
                day_select.options[0].disabled = true;
            }
            for (let day = 1; day <= days_in_month; day++) {
                if (!day_select.options[day]) {
                    day_select.options[day] = new Option(day, day, false);
                }
            }
        }
    }

    update_days() {
        const month_select = document.getElementById('month_select');
        const day_select = document.getElementById('day_select');
        const year_select = document.getElementById('year_select');
        this.set('birth_month', month_select.value);
        this.set('birth_day', day_select.value);
        this.set('birth_year', year_select.value);
        this.populate_month_select();
        let summaryVal = $('#actual_age_output_cell_value').text();
        if (summaryVal.trim() === this.language.required) {
            summaryVal = stringConstants.no_output_value_string;
        }
        $('#birthday_summary').text(summaryVal);
    }

    // Gender
    gender_changed() {
        const gender = $('#gender_select').val();
        this.set('gender', gender === '0' ? 0 : gender === '1' ? 1 : undefined);

        this.enableControls();

        this.update_gender_age_change();
        let summaryVal = $('#gender_output_cell_value').text();
        if (summaryVal.trim() === this.language.required) {
            summaryVal = '•••';
        }
        $('#gender_summary').text(summaryVal);
    }

    // Changing gender forces a recalculation of all VAC values, since many are gender sensitive.
    update_gender_age_change() {
        // Recalculate all.
        if (isset(this.current_values.gender) && isset(this.current_values.fractional_age)) {
            this.reconfigureAllInputs();
            this.refresh_calculator();
            this.configure_gender_cell();
        } else {
            this.configure_gender_cell();
        }
    }

    configure_gender_cell() {
        // Gender display is actually the icon in the birthday cell.
        const gender = this.current_values.gender;
        if (!isset(gender)) {
            $('#gender_output_cell_value').text(this.language.required);
        } else {
            if (gender === 0) {
                $('#gender_output_cell_icon').prop('src', '../images/male.png');
                $('#gender_output_cell_value').text('Male');
            } else {
                $('#gender_output_cell_icon').prop('src', '../images/female.png');
                $('#gender_output_cell_value').text('Female');
            }
        }

    }

    // Birthday
    birthday_changed(event) {
        if (event && (event.currentTarget.id === 'month_select' || event.currentTarget.id === 'year_select')) {
            this.update_days();
        }
        const month_select = document.getElementById('month_select');
        const day_select = document.getElementById('day_select');
        const year_select = document.getElementById('year_select');
        this.set('birth_month', month_select.value);
        this.set('birth_day', day_select.value);
        this.set('birth_year', year_select.value);
        if (year_select.value > 0 && month_select.value > 0 && day_select.value > 0) {
            const old_birthday = this.current_values.birthday_cell;
            this.set('birthday_cell', new Date(year_select.value, month_select.value - 1, day_select.value, 0, 0, 0));
            // Refresh everything if this is the first time birthday is set.
            this.update_age();
            $('#actual_age_output_cell_icon').prop('src', '../images/thumbs-up.png');
            if (!isset(old_birthday)) {
                this.refresh_calculator();
            } else {
                // Always update fmd risk when age changes.
                // this.fmd_changed();
                // Update output to refresh color coding.
                // this.configure_all_cells();
            }
        } else {
            this.set('birthday_cell', undefined);
            this.update_age();
        }

        this.enableControls();

        let summaryVal = $('#actual_age_output_cell_value').text();
        if (summaryVal.trim() === this.language.required) {
            summaryVal = stringConstants.no_output_value_string;
        }
        $('#birthday_summary').text(summaryVal);
        this.reconfigureAllInputs();
    }

    // Changing age forces a recalculation of FMD, and the associated control gradient.
    update_age() {
        // Upodate gender UI.
        this.configure_birthday_cell();
        // Recalculate FMD risk.
        if (this.current_values.fmd) {
            // this.reconfigureAllInputs();
            // this.fmd_changed();
        }
        // Update FMD gradient.
        if (this.current_values.fractional_age && isset(this.current_values.gender)) {
            // this.set_fmd_gradient( new EVFMDCalculator(), this.current_values["fractional_age"] );
        }
        this.update_underlying_change();
        this.calculate_vascular_age();
    }

    validate_age() {
        const month = $('#month_select').val();
        const day = $('#day_select').val();
        const year = $('#year_select').val();
        return isset(month) && month !== '' &&
            isset(day) && day !== '' &&
            isset(year) && year !== '';
    }

    configure_birthday_cell() {

        const birth_date = this.current_values.birthday_cell;
        if (!isset(birth_date)) {
            $('#actual_age_output_cell_value').html(this.language.required);
            $('#actual_age_output_cell_value').css('color', stringConstants.result_gray);
        } else {
            const start_date = moment();
            const age_timeperiod = moment_difference(moment(birth_date), moment(start_date));
            // Store the timeperiod for vascular age calculations.
            const last_birthday = moment(birth_date);
            last_birthday.add(age_timeperiod.years, 'years');
            const days = start_date.diff(moment(last_birthday), 'days');
            this.set('fractional_age', roundf(age_timeperiod.years + (days / 365.25), 6));
            const ymd_age = '' + pad(age_timeperiod.years + 'y ', 2) + pad(age_timeperiod.months, 2) + 'm ' + pad(age_timeperiod.days, 2) + 'd';
            this.set('ymd_age', ymd_age);
            this.set('chron_years', age_timeperiod.years);
            $('#actual_age_output_cell_value').html(ymd_age);
            $('#actual_age_output_cell_value').css('color', stringConstants.result_black);
        }
        if (isset(this.current_values.fractional_age)) {
            this.calculate_vascular_age();
        }
    }

    // Underlying Conditions
    underlying_changed() {
        const underlying_kidney = $('#underlying_kidney:checked').val();
        this.set('underlying_kidney', underlying_kidney);
        const underlying_cardio = $('#underlying_cardio:checked').val();
        this.set('underlying_cardio', underlying_cardio);
        const underlying_chronic_lung = $('#underlying_chronic_lung:checked').val();
        this.set('underlying_chronic_lung', underlying_chronic_lung);
        const underlying_immuno = $('#underlying_immuno:checked').val();
        this.set('underlying_immuno', underlying_immuno);
        this.update_underlying_change();
        const summaryVal = $('#underlying_output_cell_value').text();
        $('#underlying_summary').text(summaryVal);
    }

    // Changing gender forces a recalculation of all VAC values, since many are gender sensitive.
    update_underlying_change() {
        // Recalculate all.
        // this.refresh_calculator();
        // this.configure_underlying_cell();
        const response = this.calculator_4.ageChangeForUnderlying(this.current_values.underlying_kidney, this.current_values.underlying_cardio, this.current_values.underlying_chronic_lung, this.current_values.underlying_immuno, this.current_values.fractional_age, this.current_values.gender);
        this.configure_underlying_cell(response);
    }

    // Conversions:
    // http://www.ncbi.nlm.nih.gov/books/NBK83505/

    configure_underlying_cell(response) {
        const text = $('#underlying_text').text();
        const underlying_blank = this.language.underlying_blank;
        const underlying_kidney_label = this.language.underlying_kidney_label;
        const underlying_cardio_label = this.language.underlying_cardio_label;
        const underlying_chronic_lung_label = this.language.underlying_chronic_lung_label;
        const underlying_immuno_label = this.language.underlying_immuno_label;
        const underlying_none_label = this.language.underlying_none_label;
        if (isset(response) && response !== -1) {
            this.current_age_changes.underlying = response.age_change;
        } else {
            this.current_age_changes.underlying = 0;
        }
        // kidney
        if (this.current_values.underlying_kidney) {
            $('#underlying_kidney_select').text(underlying_kidney_label);
        } else {
            $('#underlying_kidney_select').text('');
        }
        // cardio
        if (this.current_values.underlying_cardio) {
            $('#underlying_cardio_select').text(underlying_cardio_label);
        } else {
            $('#underlying_cardio_select').text('');
        }
        // lung
        if (this.current_values.underlying_chronic_lung) {
            $('#underlying_chronic_lung_select').text(underlying_chronic_lung_label);
        } else {
            $('#underlying_chronic_lung_select').text('');
        }
        // immuno
        if (this.current_values.underlying_immuno) {
            $('#underlying_immuno_select').text(underlying_immuno_label);
        } else {
            $('#underlying_immuno_select').text('');
        }
        // immuno
        if (!this.current_values.underlying_kidney &&
            !this.current_values.underlying_cardio &&
            !this.current_values.underlying_chronic_lung &&
            !this.current_values.underlying_immuno) {
            $('#underlying_none_selected').text(underlying_none_label);
        } else {
            $('#underlying_none_selected').text('');
        }
        if (this.current_age_changes.underlying) {
            const age_change = this.current_age_changes.underlying;
            this.set('underlying_change', this.text_for_age_change(age_change));
            // $("#underlying_result").html("Underlying Conditions");
            $('#underlying_output_cell_value').html(this.text_for_age_change(age_change));
            $('#underlying_output_cell_value').css('color', this.color_for_age_change(this.current_values.fractional_age, age_change));
            $('#underlying_output_cell_icon').prop('src', this.icon_for_age_change(this.current_values.fractional_age, age_change));
        } else {
            $('#underlying_output_cell_value').html(stringConstants.no_output_value_string);
            $('#underlying_output_cell_value').css('color', stringConstants.result_gray);
            $('#underlying_output_cell_icon').prop('src', '../images/question-mark.png');
        }
        this.calculate_vascular_age();
    }

    // Clock
    calculate_vascular_age() {
        // Hide the intro if any values are filled in.
        if ($('#intro_overlay').is(':visible')) {
            const value_keys = Object.keys(this.current_values);
            for (let i = 0, j = value_keys.length; i < j; i++) {
                if (isset(this.current_values[value_keys[i]])) {
                    this.hide_intro();
                    if (vac_public === false) {
                        $('#save_button').prop('class', 'bordered_button');
                    }
                }
            }
        }
        const birth_date = this.current_values.birthday_cell;
        if (!isset(birth_date)) {
            $('#year_label').text(stringConstants.no_age_text + 'y');
            $('#month_label').text(stringConstants.no_age_text + 'm');
            $('#day_label').text(stringConstants.no_age_text + 'd');
            $('#year_label').css('color', stringConstants.result_gray);
            $('#month_label').css('color', stringConstants.result_gray);
            $('#day_label').css('color', stringConstants.result_gray);
            $('#year_text').css('color', stringConstants.result_gray);
            $('#month_text').css('color', stringConstants.result_gray);
            $('#day_text').css('color', stringConstants.result_gray);
            $('#vascular_age_output_cell_icon').prop('src', '../images/question-mark.png');
        } else {
            const birth_date_1 = this.current_values.birthday_cell;
            const start_date = moment();
            if (vac_public === false) {
                $('#share_button').removeClass('bordered_button_disabled').addClass('bordered_button');
            }
            // Calculate risk factors as fractional years, then adjust the start date accordingly.
            const values = Object.values(this.current_age_changes);
            let total_age_change = 0;
            for (const value of values) {
                if (isset(value)) {
                    total_age_change += value;
                }
            }
            start_date.add({days: total_age_change * 365.25});
            const age_timeperiod = moment_difference(moment(birth_date), moment(start_date));
            // Limit vascular age to range of 15 - 122.
            if (age_timeperiod.years < 15) {
                $('#year_label').text(pad(15, 2) + 'y');
                $('#month_label').text(pad(0, 2) + 'm');
                $('#day_label').text(pad(0, 2) + 'd');
                this.set('vascular_age', 15);
                this.set('vac_year', 15);
                this.set('vac_month', 0);
                this.set('vac_day', 0);
                $('#plus_icon').hide();
            } else if (age_timeperiod.years >= 122) {
                $('#year_label').text(pad(122, 2) + 'y');
                $('#month_label').text(pad(0, 2) + 'm');
                $('#day_label').text(pad(0, 2) + 'd');
                this.set('vascular_age', 122);
                this.set('vac_year', 122);
                this.set('vac_month', 0);
                this.set('vac_day', 0);
                $('#plus_icon').show();
            } else {
                $('#year_label').text(pad(age_timeperiod.years, 2) + 'y');
                $('#month_label').text(pad(age_timeperiod.months, 2) + 'm');
                $('#day_label').text(pad(age_timeperiod.days, 2) + 'd');
                this.set('vascular_age', roundf(this.current_values.fractional_age + total_age_change, 6));
                this.set('vac_year', age_timeperiod.years);
                this.set('vac_month', age_timeperiod.months);
                this.set('vac_day', age_timeperiod.days);
                $('#plus_icon').hide();
            }
            const clock_color = this.color_for_age_change(this.current_values.fractional_age, total_age_change);
            $('#year_label').css('color', clock_color);
            $('#month_label').css('color', clock_color);
            $('#day_label').css('color', clock_color);
            $('#year_text').css('color', stringConstants.result_gray);
            $('#month_text').css('color', stringConstants.result_gray);
            $('#day_text').css('color', stringConstants.result_gray);
            $('#vascular_age_output_cell_icon').prop('src', this.icon_for_age_change(this.current_values.fractional_age, total_age_change));
            // calculate hospitalization risk
            const hospital_risk = this.calculate_risk('hospital', this.current_values.vac_year);
            const hospital_color = this.color_for_risk_change(this.current_values.vac_year);
            // high end
            /*if (hospital_risk > 95) {
                $('#hospital_value').text(95);
                this.set('hospital', 95);
            } else if (hospital_risk <= 0.1) {
                $('#hospital_value').text(0.1);
                this.set('hospital', 0.1);
            } else if (hospital_risk < 10) {
                if (hospital_risk < 1) {
                    $('#hospital_value').text(hospital_risk.toFixed(2));
                    this.set('hospital', hospital_risk.toFixed(2));
                } else {
                    $('#hospital_value').text(hospital_risk.toFixed(2));
                    this.set('hospital', hospital_risk.toFixed(2));
                }
            } else {
                $('#hospital_value').text(hospital_risk.toFixed(0));
                this.set('hospital', hospital_risk.toFixed(0));
            }*/
            if (hospital_risk < .01) {
                $('#hospital_value').text(hospital_risk.toFixed(3));
                this.set('hospital', hospital_risk.toFixed(3));
            } else if (hospital_risk < 1) {
                $('#hospital_value').text(hospital_risk.toFixed(2));
                this.set('hospital', hospital_risk.toFixed(2));
            }else {
                $('#hospital_value').text(hospital_risk.toFixed(1));
                this.set('hospital', hospital_risk.toFixed(1));
            }
            $('#hospital_value').css('color', hospital_color);
            $('#hospital_percent').css('color', hospital_color);
            // ---------------------
            // calculate icu risk
            const icu_risk = this.calculate_risk('icu', this.current_values.vac_year);
            const icu_color = this.color_for_risk_change(this.current_values.vac_year);
            // high end
           /* if (icu_risk > 95) {
                $('#icu_value').text(95);
                this.set('icu', 95);
            } else if (icu_risk <= 0.01) {
                $('#icu_value').text(0.01);
                this.set('icu', 0.01);
            } else if (icu_risk < 10) {
                if (icu_risk < 1) {
                    $('#icu_value').text(icu_risk.toFixed(2));
                    this.set('icu', icu_risk.toFixed(2));
                } else {
                    $('#icu_value').text(icu_risk.toFixed(1));
                    this.set('icu', icu_risk.toFixed(1));
                }
            } else {
                $('#icu_value').text(icu_risk.toFixed(0));
                this.set('icu', icu_risk.toFixed(0));
            }*/
            if (icu_risk < .01) {
                $('#icu_value').text(icu_risk.toFixed(3));
                this.set('icu', icu_risk.toFixed(3));
            } else if (icu_risk < 1) {
                $('#icu_value').text(icu_risk.toFixed(2));
                this.set('icu', icu_risk.toFixed(2));
            }else {
                $('#icu_value').text(icu_risk.toFixed(1));
                this.set('icu', icu_risk.toFixed(1));
            }
            $('#icu_value').css('color', icu_color);
            $('#icu_percent').css('color', icu_color);
            // ---------------------
            // calculate death risk
            const death_risk = this.calculate_risk('death', this.current_values.vac_year);
            const death_color = this.color_for_risk_change(this.current_values.vac_year);
            /*if (death_risk > 95) {
                $('#death_value').text(95);
                this.set('death', 95);
            } else if (death_risk <= 0.01) {
                $('#death_value').text(0.1);
                this.set('death', 0.01);
            } else {
                if (death_risk < 10) {
                    if (death_risk < 1) {
                        $('#death_value').text(death_risk.toFixed(2));
                        this.set('death', death_risk.toFixed(2));
                    } else {
                        $('#death_value').text(death_risk.toFixed(1));
                        this.set('death', death_risk.toFixed(1));
                    }
                } else {
                    $('#death_value').text(death_risk.toFixed(0));
                    this.set('death', death_risk.toFixed(0));
                }
            }*/
            if (death_risk < .01) {
                $('#death_value').text(death_risk.toFixed(3));
                this.set('death', death_risk.toFixed(3));
            }
            else if (death_risk < 1) {
                $('#death_value').text(death_risk.toFixed(2));
                this.set('death', death_risk.toFixed(2));
            } else {
                $('#death_value').text(death_risk.toFixed(1));
                this.set('death', death_risk.toFixed(1));
            }
            $('#death_value').css('color', death_color);
            $('#death_percent').css('color', death_color);
            // ---------------------
            // calculate death change
            const chron_risk = this.calculate_risk('chron', this.current_values.chron_years);
            let mortality_change = (chron_risk.toFixed(2) - death_risk.toFixed(2)) / chron_risk.toFixed(2);
            mortality_change = mortality_change.toFixed(2);
            let mortality_text = '';
            let final_num = '';
            if (mortality_change === 0) {
                mortality_text = 'Mortality stays the same.';
                $('#mortality-arrow').html('');
            } else if (mortality_change > 0) {
                final_num = mortality_change * 100;
                mortality_text = 'Mortality decreased by ' + final_num + '%';
                $('#mortality-arrow').html('<i class=\'fas fa-arrow-down\'></i>');
            } else {
                final_num = Math.abs(mortality_change);
                final_num = roundf(final_num * 100);
                final_num = final_num;
                mortality_text = 'Mortality increased by ' + final_num + '% ';
                $('#mortality-arrow').html('<i class=\'fas fa-arrow-up\'></i>');
            }
            $('#chron_death_change').text(mortality_text);
            // ---------------------
        }
    }

    // Convert values that have already been entered to the new units.

    calculate_risk(type, numyears) {
        // let years = this.current_values['vac_year'];
        const years = numyears;
        const omicronAdjuster = 10;

        let risk = 0;
        // Hospitalization
        if (type === 'hospital') {
            risk += -1.536 + (1.428 * Math.log10(years));
            risk = Math.pow(10, risk);
        } else if (type === 'hospital_low') {
            risk += -1.130 + (1.410 * Math.log10(years));
            risk = Math.pow(10, risk);
        }
        // ICU
        else if (type === 'icu') {
            risk += -3.933 + (2.513 * Math.log10(years));
            risk = Math.pow(10, risk);
        } else if (type === 'icu_low') {
            risk += -3.150 + (2.213 * Math.log10(years));
            risk = Math.pow(10, risk);
        }
        // Mortality/Death
        else if (type === 'death') {
            console.log(years);
            risk += -1.967 + (0.0288 * years);
            risk = Math.pow(10, risk);
        } else if (type === 'death_low') {
            console.log(years);
            risk += -1.506 + (0.0263 * years);
            risk = Math.pow(10, risk);
        }
        // Chronological mortality
        else if (type === 'chron') {
            risk += -1.967 + (0.0288 * years);
            risk = Math.pow(10, risk);
        }

        return risk / 1.67 / omicronAdjuster;
        // return risk;
    }

    // Color codes as a percentage of actual age.

    // Formatting
    // age_change is in years (ex. 9.43)
    text_for_age_change(age_change) {
        const yearDays = 365.25;
        const yearMonths = 12;
        const monthDays = yearDays / yearMonths;
        let text = '';
        if (age_change > 0) {
            text = '+ ';
        } else if (age_change < 0) {
            text = '- ';
        }
        const abs_change = Math.abs(age_change);
        const years = floorf(abs_change);
        let remainder = ((abs_change - years) * yearMonths);
        const months = floorf(remainder);
        remainder = ((remainder - months) * monthDays);
        const days = floorf(remainder);
        text += pad(years, 2) + 'y ' + pad(months, 2) + 'm ' + pad(days, 2) + 'd';
        return text;
    }

    // Version 3.1 - changed to actual age change.
    color_for_age_change(age, age_change, slider) {
        let color = stringConstants.result_gray;
        if (age_change < -2) {
            color = !slider ? stringConstants.result_blue : stringConstants.result_blue_slider;
        } else if (age_change <= 0) {
            color = !slider ? stringConstants.result_green : stringConstants.result_green_slider;
        } else if (age_change < 2) {
            color = !slider ? stringConstants.result_orange : stringConstants.result_orange_slider;
        } else {
            color = !slider ? stringConstants.result_red : stringConstants.result_red_slider;
        }
        return color;
    }

    // Icon URLs for percentage of actual age.

    color_for_risk_change(age) {
        let color = stringConstants.result_gray;
        if (age > 65) {
            color = stringConstants.result_red;
        } else if (age > 40 && age <= 65) {
            color = stringConstants.result_orange;
        } else {
            color = stringConstants.result_green;
        }
        return color;
    }

    // Refreshes the collection of age changes, and reconfigures all the output cells.

    // Version 3.1 - changed to actual age change.
    icon_for_age_change(age, age_change) {
        let icon_url = '../images/question-mark.png';
        if (age_change < -2) {
            icon_url = '../images/trophy.png';
        } else if (age_change <= 0) {
            icon_url = '../images/thumbs-up.png';
        } else if (age_change < 2) {
            icon_url = '../images/exclamation.png';
        } else {
            icon_url = '../images/thumbs-down.png';
        }
        return icon_url;
    }

    // Call when loading a saved dataset, and when gender changes.
    refresh_calculator() {
        this.bmiManager.updateBmiAgeInfluence();
        this.waistManager.updateWaistAgeInfluence();
        this.smokingManager.updateSmokingAgeInfluence();
        this.bpManager.updateBpAgeInfluence();
        this.vitaminDManager.updateVitaminDAgeInfluence();
        this.fmdManager.updateFmdAgeInfluence();
        this.a1cManager.updateA1cAgeInfluence();
        this.lipidsManager.updateLipidsAgeInfluence();

    }

    clear_inputs() {
        // TODO: Consider the default units to be conditional on the region or partner
        $('#global_units_select').val('imperial');

        this.clear_sessionStorage();

        this.resetCurrentValues();

        this.current_age_changes = {};

        $('#gender_select').val('');
        this.gender_changed();
        this.configure_gender_cell();

        $('#month_select').val('');
        $('#day_select').val('');
        $('#year_select').val('');
        this.update_days();
        this.birthday_changed();
        this.configure_birthday_cell();

        $('#systolic_blood_pressure').val('');
        $('#diastolic_blood_pressure').val('');

        $('#email').val('');
        $('#notes').val('');
        $('#optin').prop('checked', false);
        $('#underlying_kidney').prop('checked', false);
        $('#underlying_cardio').prop('checked', false);
        $('#underlying_chronic_lung').prop('checked', false);
        $('#underlying_immuno').prop('checked', false);
        this.underlying_changed();
        this.configure_underlying_cell();

        $('#hospital_value').text(0);
        $('#hospital_value').css('color', stringConstants.result_gray);
        $('#hospital_percent').css('color', stringConstants.result_gray);
        $('#icu_value').text(0);
        $('#icu_value').css('color', stringConstants.result_gray);
        $('#icu_percent').css('color', stringConstants.result_gray);
        $('#death_value').text(0);
        $('#death_value').css('color', stringConstants.result_gray);
        $('#death_percent').css('color', stringConstants.result_gray);
        this.resetCurrentValues();
        this.calculate_vascular_age();
        this.reconfigureAllInputs();
        this.refresh_calculator();
        this.updateAllHighlightEmptyRequiredFields();
    }

    // Data validation
    validate_input(element_id, value, ui_element, show_alert) {
        if (!isset(show_alert)) {
            show_alert = true;
        }
        let input_valid = true;
        let error_string = '';
        let error = false;
        const constraints = this.constraints[element_id];

        // Clear empty input values
        // TODO: I'm not sure I want this to do this here, what's the effect of removing it
        if (isNaN(value)) {
            value = undefined;
            this.current_values[element_id] = undefined;
        }

        if (isset(value)) {
            const min = typeof constraints.min === 'function' ? constraints.min(this.current_values) : constraints.min;
            const max = typeof constraints.max === 'function' ? constraints.max(this.current_values) : constraints.max;
            if (!between(value, min, max)) {
                error = true;
                error_string = typeof constraints.message === 'function' ? constraints.message(this.current_values) : constraints.message;
            }
        }
        if (error) {
            input_valid = false;
            if (show_alert && error_string) {
                const alert_dialog_4 = new AlertDialog(error_string, ui_element);
            }
        }
        return input_valid;
    }

    clear_sessionStorage() {
        if (typeof (window.sessionStorage) !== 'undefined') {
            window.sessionStorage.removeItem('terms_accepted');
            window.sessionStorage.removeItem('zip');
            window.sessionStorage.removeItem('country');
            window.sessionStorage.removeItem('save_clicked');
        }
    }

    // Overlay dialogs.
    open_dialog(dialog_id) {
        $('.modal').each((index, modal) => {
            this.close_dialog(modal.id);
        });
        if (dialog_id === 'email_overlay') {
            document.getElementById('fade').style.display = 'block';
            document.getElementById(dialog_id).style.display = 'block';
        } else {
            $('#' + dialog_id).modal('show');
        }
    }

    // Intro

    close_dialog(dialog_id) {
        $('#' + dialog_id).modal('hide');
    }

    // Printing
    print_report() {
        window.print();
    }

    reconfigureAllInputs() {
        this.bmiManager.reconfigureInputs();
        this.waistManager.reconfigureWaistInput();
        this.smokingManager.reconfigureSmokingInput();
        this.bpManager.reconfigureBpInputs();
        this.vitaminDManager.reconfigureVitaminDInput();
        this.fmdManager.reconfigureFmdInput();
        this.a1cManager.reconfigureA1cInput();
        this.lipidsManager.reconfigureLipidInputs();

    }

    // Need to make this method smaller, simpler, more generic and outside the controller
    generateSliderGradient(value, element, constraints) {
        if (value === 'systolic_blood_pressure') {
            // Really don't like this, but best override I have for now that doesn't involve over-customizing the manager helper
            value = this.current_values.blood_pressure_treated ? 'bpt' : 'bp';
        }
        // Scale the gradient to account for the background on the range input being wider than the slider area
        const sliderBuffer = 3;
        const sliderMax = 100 - (sliderBuffer * 2);
        const $element = $(element);

        /*
         * Lipids TG and HDLC are defaulted to the half-way point between min and max so that the gradients
         *   are filled in even when one or both of the values are unset so it doesn't look like they're disabled.
         */
        let lipids_hdlc = isset(this.current_values.lipids_hdlc) ? this.current_values.lipids_hdlc : ((this.constraints.lipids_hdlc.min(this.current_values, true) + this.constraints.lipids_hdlc.max(this.current_values, true)) / 2);
        let lipids_tg = isset(this.current_values.lipids_tg) ? this.current_values.lipids_tg : ((this.constraints.lipids_tg.min(this.current_values, true) + this.constraints.lipids_tg.max(this.current_values, true)) / 2);

        if (!isset(this.current_values.fractional_age) || !isset(this.current_values.gender)) {
            $element.css('background-image', '');
            return;
        }

        if ((value === 'bmi' || value === 'weight') && !isset(this.current_values.height)) {
            $element.css('background-image', '');
            return;
        }

        const gradient = [];
        const min = typeof constraints.min === 'function' ? constraints.min(this.current_values, true) : constraints.min;
        const max = typeof constraints.max === 'function' ? constraints.max(this.current_values, true) : constraints.max;
        const precision = typeof constraints.precision === 'function' ? constraints.precision(this.current_values, true) : constraints.precision;
        // Convert precision (0, 1, 2) to decimal places (1, 0.1, 0.01)
        const step = 1 / (10 ** precision);

        let gradientCacheKey = `${value}-${element}-${min}-${max}-${precision}`;
        switch (value) {
            case 'fmd':
                // FMD is influenced by fractional age (user's birthdate)
                gradientCacheKey += `-fa-${this.current_values.fractional_age}`;
                break;
            case 'bp':
            case 'bpt':
            case 'waist':
                // Waist and Blood Pressure are influenced by gender
                gradientCacheKey += `-gender-${this.current_values.gender}`;
                break;
            case 'bmi':
            case 'weight':
                // BMI and Weight are influenced by height
                gradientCacheKey += `-height-${this.current_values.height}`;
                break;
            case 'lipids_tg':
            case 'lipids_hdlc':
                // Lipids TG is influenced by HDLC
                gradientCacheKey += `-hdlc-${lipids_hdlc}`;
                // HDLC is influenced by Lipids TG
                gradientCacheKey += `-tg-${lipids_tg}`;
                break;
        }

        let style = this.gradientCache[gradientCacheKey];
        if (!style) {
            let last = null;
            let i = min;
            while (i <= max) {
                let response = null;
                /*
                 * This whole function needs to be broken out, but especially this part should be removed
                 *   and passed in parameter or configurable so it doesn't have to run this check on every loop.
                 */
                if (value === 'vitamin_d') {
                    response = this.calculator_4.ageChangeForVitaminD(i, 'si', this.current_values.fractional_age, this.current_values.gender);
                } else if (value === 'weight') {
                    // Checking for height being set above
                    let bmi = bmiFromHeightAndWeight(this.current_values.height, i);
                    bmi = Math.max(bmi, this.constraints.bmi.min);
                    bmi = Math.min(bmi, this.constraints.bmi.max);
                    response = this.calculator_4.ageChangeForValue('bmi', bmi, this.current_values.fractional_age, this.current_values.gender);
                } else if (value === 'lipids_tg') {
                    response = this.calculator_4.ageChangeForLipids(i, lipids_hdlc, 'si');
                } else if (value === 'lipids_hdlc') {
                    response = this.calculator_4.ageChangeForLipids(lipids_tg, i, 'si');
                } else {
                    response = this.calculator_4.ageChangeForValue(value, i, this.current_values.fractional_age, this.current_values.gender);
                }
                const color = this.color_for_age_change(this.current_values.fractional_age, response.age_change, true);
                /*
                 * We want sharp color changes, not blending, so create a color boundary as soon as the value changes,
                 *   as well as colors specified at the lower and upper bounds
                 */
                if (color !== last || i <= min || i >= max) {
                    // Higher precision than we need but we have it so might as well use some of it
                    const percent = roundf((((i - min) / (max - min)) * sliderMax) + sliderBuffer, 6);
                    if (last && last !== color) {
                        gradient.push({value: i, percent, color: last});
                    }
                    gradient.push({value: i, percent, color});
                    last = color;
                }
                // Round to chop off floating point and rounding errors so we can actually land on max
                i = roundf(i + step, 6);
            }
            style = `linear-gradient(to right, ${gradient.map((v) => `${v.color} ${v.percent}%`).join(', ')})`;
            this.gradientCache[gradientCacheKey] = style;
        } else {
            // Spot for debugging gradient cache
        }
        if ($element.css('background-image') !== style) {
            $element.css('background-image', style);
            this.gradientCache[gradientCacheKey] = style;
            // Style did change
            return true;
        }
        // Style didn't change
        return false;
    }

    global_units_changed(event) {
        this.set('imperial', $('#global_units_select').val() === 'imperial');
        this.reconfigureAllInputs();
        this.refresh_calculator();
    }

    populateFetchedRecord(response) {
        const data = mapServerToClientValues(response[0] || response);

        this.resetCurrentValues();

        Object.assign(this.current_values, data);

        $("#global_units_select").val(this.current_values.imperial ? 'imperial' : 'metric');

        // set birthdates
        $("#month_select").val(data.birth_month);
        $("#day_select").val(data.birth_day);
        $("#year_select").val(data.birth_year);
        this.update_days();
        this.birthday_changed();

        // set underlying conditions
        if (data.chronic_kidney === 1) {
            $("#underlying_kidney").prop("checked", true);
        } else {
            $("#underlying_kidney").prop("checked", false);
        }
        if (data.cardiovascular_disease === 1) {
            $("#underlying_cardio").prop("checked", true);
        } else {
            $("#underlying_cardio").prop("checked", false);
        }
        if (data.chronic_lung === 1) {
            $("#underlying_chronic_lung").prop("checked", true);
        } else {
            $("#underlying_chronic_lung").prop("checked", false);
        }
        if (data.immuno_condition === 1) {
            $("#underlying_immuno").prop("checked", true);
        } else {
            $("#underlying_immuno").prop("checked", false);
        }
        this.underlying_changed()

        $('#gender_select').val(data.gender);
        this.gender_changed();

        // Since this is a fresh record on page load, it's not dirty
        this.isDirty = false;

        this.updateAllHighlightEmptyRequiredFields();
    }

    saveData(event) {
        event.preventDefault();
        if (typeof (window.sessionStorage) !== 'undefined') {
            window.sessionStorage.removeItem('save_clicked');
        }
        if (!this.validate_age()) {
            this.open_dialog('dobRequiredModal');
            return;
        }

        const CSRF_TOKEN = $('meta[name="csrf_token"]').prop('content');
        const saveDataUrl = $('meta[name="saveDataUrl"]').prop('content');
        const partnerSubdomain = $('meta[name="partnerSubdomain"]').prop('content');

        const app_data = mapClientToServerValues(this.current_values);
        const buttonId = event.currentTarget.getAttribute('id');

        if (typeof (window.sessionStorage) !== 'undefined') {
            window.sessionStorage.removeItem('save_clicked');
        }

        if (this.isDirty || buttonId === 'save_button' || buttonId === 'share_button') {
            $.ajax({
                url: saveDataUrl,
                type: 'post',
                data: {_token: CSRF_TOKEN, subdomain: partnerSubdomain, data: app_data, link: buttonId},
                success: response => {
                    if (response.link === 'save_button' || response.link === 'share_button' || response.link === 'dietid_link') {
                        $('#savedModal').modal('show');
                    } else if (response.link === 'symptoms_tracker_link') {
                        $('#symptomsModal').modal('show');
                    } else {
                        if (typeof (window.sessionStorage) !== 'undefined') {
                            window.sessionStorage.setItem('save_clicked', buttonId);
                        }
                        $('#loginModal #savedata').val(1);
                        $("#loginModal").modal('show');
                    }
                    this.isDirty = false;
                }
            });
        } else if (buttonId === 'dietid_link') {
            this.openDietId();
        } else if (buttonId === 'symptoms_tracker_link') {
            this.openSymptomsTracker();
        }
    }

    enableControls(enable = (isset(this.current_values.gender) && isset(this.current_values.fractional_age))) {
        this.a1cManager.enableControls(enable);
        this.bmiManager.enableControls(enable);
        this.fmdManager.enableControls(enable);
        this.lipidsManager.enableControls(enable);
        this.smokingManager.enableControls(enable);
        this.bpManager.enableControls(enable);
        this.vitaminDManager.enableControls(enable);
        this.waistManager.enableControls(enable);

        $('#underlying_kidney').prop('disabled', !enable);
        $('#underlying_cardio').prop('disabled', !enable);
        $('#underlying_chronic_lung').prop('disabled', !enable);
        $('#underlying_immuno').prop('disabled', !enable);

    }

    resetCurrentValues() {
        this.current_values = {
            imperial: $('#global_units_select').val() === 'imperial',
            zip: window.sessionStorage.getItem('zip')
        };

    }

    showInputFields(show = true) {
        const fields = $('input[type=number]').parent().parent();
        if (show) {
            fields.removeClass('d-none')
        } else {
            fields.addClass('d-none')
        }
    }

    bindHighlightEmptyRequiredFields() {
        const elements = document.querySelectorAll('[required]');
        elements.forEach((element) => {
            element.addEventListener('change', this.handleHighlightEmptyRequiredFields);
        });
        this.updateAllHighlightEmptyRequiredFields();
    }

    handleHighlightEmptyRequiredFields(eventOrElement) {
        const target = eventOrElement.srcElement || eventOrElement;
        const parent = target.parentElement;
        [...parent.children].forEach((child) => {
            const tagName = child.tagName.toLowerCase();
            const value = target.value.replace(/-/g, '').trim() && target.type !== 'checkbox';
            const checked = target.checked;
            if (!value && !checked) {
                if (tagName.toLowerCase() === 'select' || tagName.toLowerCase() === 'input' || tagName.toLowerCase() === 'button') {
                    child.classList.add('border-danger');
                } else {
                    child.classList.add('text-danger');
                }
            } else {
                if (tagName.toLowerCase() === 'select' || tagName.toLowerCase() === 'input' || tagName.toLowerCase() === 'button') {
                    child.classList.remove('border-danger');
                } else {
                    child.classList.remove('text-danger');
                }
            }
        });
    }

    updateAllHighlightEmptyRequiredFields() {
        const elements = document.querySelectorAll('[required]');
        elements.forEach((element) => {
            this.handleHighlightEmptyRequiredFields(element);
        });
    }
}
global.CalculatorController = CalculatorController;
